/** Words for the portability import page (persona targets, credential sets, refusals). */
import type { TargetAction } from '@/services/portability'

export const TARGET_LABELS: Record<TargetAction, string> = {
  create: 'Create new',
  update: 'Update an existing persona',
  skip: 'Skip',
}

export const TARGET_GROUP_LABEL = 'What to do with this persona'
export const UPDATE_SELECT_LABEL = 'Persona to update'
export const SAME_NAME = 'same name'
export const MATCHES_ARCHIVE = 'matches this archive'
export const NO_CANDIDATES = 'No persona in this environment can take an update.'

export const BUNDLE_CHANGES = 'Bundle changes'
export const BUNDLE_SAME = 'Bundle is the same; nothing is written'

export function updateLine(name: string): string {
  return `Adds a new version to ${name}; its settings stay as they are. Undo from the Workshop's Version history.`
}

export const CREDENTIAL_HEADING = 'Credential sets'
export const LEAVE_UNMAPPED = 'Leave unmapped'
export const CREDENTIAL_HINT =
  'Updates keep the set the persona already uses; new personas get no set (deployment defaults)'

export function bindingsLine(count: number, name: string): string {
  const verb = count === 1 ? 'binding names' : 'bindings name'
  return `${count} ${verb} ${name}, which this environment does not have`
}

export function credentialSelectLabel(name: string): string {
  return `Local set for ${name}`
}

export function targetSelectLabel(key: string): string {
  return `${UPDATE_SELECT_LABEL}: ${key}`
}

/** One sentence per refusal code the import routes answer with; each names the key. */
const CODE_WORDS: Record<string, (key: string) => string> = {
  persona_target_invalid: (key) =>
    `The choice for ${key} cannot be used: pick a persona in the target owner's account to update, or choose another action.`,
  persona_target_duplicate: (key) =>
    `${key} would update a persona that another persona in this archive also updates; give each archive persona its own target.`,
  credential_set_unknown: (key) =>
    `The credential set chosen for ${key} does not exist in this environment; pick another or leave it unmapped.`,
}

const UNKNOWN_CODE = (key: string) => `The import choices were not accepted (${key}).`

/**
 * The line for a failed request: a string detail as written, a `{code, key}` detail in words.
 * `nameFor` names the persona a key stands for when the page knows it; else the key is used.
 */
export function errorText(e: unknown, nameFor?: (key: string) => string | undefined): string {
  const detail = e && typeof e === 'object' && 'detail' in e ? (e as { detail: unknown }).detail : null
  if (typeof detail === 'string' && detail.trim()) return detail
  if (detail && typeof detail === 'object' && 'code' in detail) {
    const { code, key } = detail as { code?: unknown; key?: unknown }
    const k = typeof key === 'string' ? (nameFor?.(key) || key) : 'an item'
    const words = typeof code === 'string' ? CODE_WORDS[code] : undefined
    return (words ?? UNKNOWN_CODE)(k)
  }
  return e instanceof Error ? e.message : String(e)
}
